import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	where,
} from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import './MyAvailability.css';

// Weekly availability + dated time off for ONE teacher record. Mounted twice:
// the teacher dashboard's Availability tab (own teacherId, resolved from
// users/{uid}.teacherId — same mapping TeacherSchedule uses) and the admin
// Teacher Scheduling page (any teacher, behind a picker). All writes go through
// the setTeacherAvailability / addTeacherTimeOff / removeTeacherTimeOff
// callables — `teachers` and `teacherTimeOff` stay client-write-denied, and the
// server enforces self-or-admin, so this component never needs to know which
// role is driving it.
//
// Weekly "Not available" is a SOFT signal by design: the generator penalizes
// but can still assign a sole-candidate day, and the gap surfaces in admin
// review instead of as an uncovered lesson. Dated time off is the HARD block.
// The copy below says so, so a teacher is never surprised by an assignment on
// a day they marked off weekly.

const DAY_KEYS = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
];
const DAY_LABELS = {
	sunday: 'Sunday',
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
};

// Local calendar day as "YYYY-MM-DD" — NOT toISOString().slice (the UTC-shift
// trap: in the evening US Central, toISOString is already tomorrow).
function todayKey() {
	const now = new Date();
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, '0');
	const d = String(now.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

// "HH:MM" wall time → "3:00 PM". Built from parts, never new Date(string).
function formatTimeKey(t) {
	const [h, m] = (t || '').split(':').map(Number);
	if (Number.isNaN(h) || Number.isNaN(m)) return t || '';
	return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit',
	});
}

// "YYYY-MM-DD" → "Mon, Sep 7" without new Date(string) (UTC-midnight trap).
function formatDateKey(key) {
	const [y, m, d] = (key || '').split('-').map(Number);
	if (!y || !m || !d) return key || '';
	return new Date(y, m - 1, d).toLocaleDateString(undefined, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
	});
}

function MyAvailability({ teacherId, teacherName, onChanged }) {
	const [status, setStatus] = useState('loading'); // loading | ready | error
	const [grid, setGrid] = useState({}); // day → true|false (windows render as true + note)
	const [blocks, setBlocks] = useState({}); // day → [{start,end}] weekly blocked chunks
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState(null);
	const [error, setError] = useState(null);

	// Weekly blocked-chunk entry form.
	const [blockDay, setBlockDay] = useState('sunday');
	const [blockStart, setBlockStart] = useState('');
	const [blockEnd, setBlockEnd] = useState('');

	const [timeOff, setTimeOff] = useState([]);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [offStart, setOffStart] = useState(''); // optional partial-day times
	const [offEnd, setOffEnd] = useState('');
	const [reason, setReason] = useState('');
	const [savingTimeOff, setSavingTimeOff] = useState(false);

	const loadTimeOff = useCallback(async () => {
		// Equality-only query + client sort: an orderBy here would demand a
		// composite index for a collection of a few dozen docs.
		const snapshot = await getDocs(
			query(collection(db, 'teacherTimeOff'), where('teacherId', '==', teacherId))
		);
		const today = todayKey();
		const entries = snapshot.docs
			.map((tfDoc) => ({ id: tfDoc.id, ...tfDoc.data() }))
			.filter((entry) => entry.date >= today)
			.sort((a, b) => (a.date < b.date ? -1 : 1));
		setTimeOff(entries);
	}, [teacherId]);

	useEffect(() => {
		let alive = true;
		if (!teacherId) {
			setStatus('error');
			setError(
				'Your account is not linked to a teacher record yet — ask an admin.'
			);
			return undefined;
		}
		(async () => {
			try {
				const teacherDoc = await getDoc(doc(db, 'teachers', teacherId));
				if (!alive) return;
				const availability = teacherDoc.exists()
					? teacherDoc.data().availability || {}
					: {};
				const next = {};
				for (const day of DAY_KEYS) {
					// Windows count as available at day level; the day toggle
					// overwrites them, which is fine — the UI is the only writer.
					next[day] = availability[day] !== false;
				}
				setGrid(next);
				setBlocks(
					teacherDoc.exists()
						? teacherDoc.data().unavailableBlocks || {}
						: {}
				);
				await loadTimeOff();
				if (alive) setStatus('ready');
			} catch (err) {
				console.error('Failed to load availability:', err);
				if (alive) {
					setStatus('error');
					setError('Could not load availability.');
				}
			}
		})();
		return () => {
			alive = false;
		};
	}, [teacherId, loadTimeOff]);

	const canSave = status === 'ready' && dirty && !saving;
	const handleSave = async () => {
		if (!canSave) return; // handler guard — disabled alone is not a guard
		setSaving(true);
		setError(null);
		setMessage(null);
		try {
			const availability = {};
			for (const day of DAY_KEYS) availability[day] = grid[day] === true;
			await httpsCallable(functions, 'setTeacherAvailability')({
				teacherId,
				availability,
				unavailableBlocks: blocks,
			});
			setDirty(false);
			setMessage('Availability saved.');
			if (onChanged) onChanged();
		} catch (err) {
			console.error('Failed to save availability:', err);
			setError(err.message || 'Could not save availability.');
		} finally {
			setSaving(false);
		}
	};

	const canAddBlock = status === 'ready' && blockStart && blockEnd && blockEnd > blockStart;
	const handleAddBlock = () => {
		if (!canAddBlock) return;
		setBlocks((b) => ({
			...b,
			[blockDay]: [...(b[blockDay] || []), { start: blockStart, end: blockEnd }],
		}));
		setBlockStart('');
		setBlockEnd('');
		setDirty(true);
	};
	const handleRemoveBlock = (day, index) => {
		setBlocks((b) => ({
			...b,
			[day]: (b[day] || []).filter((_, i) => i !== index),
		}));
		setDirty(true);
	};

	const timedOff = Boolean(offStart || offEnd);
	const timedOffValid =
		!timedOff ||
		(offStart && offEnd && offEnd > offStart && (!endDate || endDate === startDate));
	const canAddTimeOff =
		status === 'ready' && startDate && !savingTimeOff && timedOffValid;
	const handleAddTimeOff = async () => {
		if (!canAddTimeOff) return;
		setSavingTimeOff(true);
		setError(null);
		setMessage(null);
		try {
			const result = await httpsCallable(functions, 'addTeacherTimeOff')({
				teacherId,
				startDate,
				endDate: endDate || startDate,
				reason: reason.trim() || null,
				...(timedOff ? { startTime: offStart, endTime: offEnd } : {}),
			});
			const { added = 0, skipped = 0 } = result.data || {};
			setMessage(
				`Added ${added} day${added === 1 ? '' : 's'} off${
					skipped ? ` (${skipped} already entered)` : ''
				}.`
			);
			setStartDate('');
			setEndDate('');
			setOffStart('');
			setOffEnd('');
			setReason('');
			await loadTimeOff();
			if (onChanged) onChanged();
		} catch (err) {
			console.error('Failed to add time off:', err);
			setError(err.message || 'Could not add time off.');
		} finally {
			setSavingTimeOff(false);
		}
	};

	const handleRemoveTimeOff = async (entryId) => {
		setError(null);
		try {
			await httpsCallable(functions, 'removeTeacherTimeOff')({ entryId });
			await loadTimeOff();
			if (onChanged) onChanged();
		} catch (err) {
			console.error('Failed to remove time off:', err);
			setError(err.message || 'Could not remove that entry.');
		}
	};

	const title = useMemo(
		() => (teacherName ? `${teacherName}'s availability` : 'My availability'),
		[teacherName]
	);

	if (status === 'loading') {
		return <div className='td-state'>Loading availability…</div>;
	}
	if (status === 'error') {
		return <div className='td-state td-state--error'>{error}</div>;
	}

	return (
		<div className='mya'>
			<section className='mya__section'>
				<h2 className='mya__title'>{title}</h2>
				<p className='mya__hint'>
					Days marked "Not available" are avoided when the schedule is
					generated. If a day has no other coverage you may still be
					asked — specific days off below are never scheduled.
				</p>
				<div className='mya__grid'>
					{DAY_KEYS.map((day) => {
						const available = grid[day] === true;
						return (
							<div key={day} className='mya__row'>
								<span className='mya__day'>{DAY_LABELS[day]}</span>
								<div
									className='mya__seg'
									role='group'
									aria-label={`${DAY_LABELS[day]} availability`}>
									<button
										type='button'
										className={`mya__segbtn${
											available ? ' is-on' : ''
										}`}
										aria-pressed={available}
										onClick={() => {
											setGrid((g) => ({ ...g, [day]: true }));
											setDirty(true);
										}}>
										Available
									</button>
									<button
										type='button'
										className={`mya__segbtn mya__segbtn--off${
											!available ? ' is-on' : ''
										}`}
										aria-pressed={!available}
										onClick={() => {
											setGrid((g) => ({ ...g, [day]: false }));
											setDirty(true);
										}}>
										Not available
									</button>
								</div>
							</div>
						);
					})}
				</div>
				<h3 className='mya__subtitle'>Blocked times</h3>
				<p className='mya__hint'>
					Chunks of a day you can never teach — a standing class, a
					weekly conflict. These are firm: if a day's lessons overlap
					a blocked time, that day is assigned to another teacher.
					Splitting one day between teachers is something Anthony or
					Grace do by hand.
				</p>
				<div className='mya__toform'>
					<label className='mya__field'>
						<span>Day</span>
						<select
							value={blockDay}
							onChange={(e) => setBlockDay(e.target.value)}>
							{DAY_KEYS.map((day) => (
								<option key={day} value={day}>
									{DAY_LABELS[day]}
								</option>
							))}
						</select>
					</label>
					<label className='mya__field'>
						<span>From</span>
						<input
							type='time'
							value={blockStart}
							onChange={(e) => setBlockStart(e.target.value)}
						/>
					</label>
					<label className='mya__field'>
						<span>Until</span>
						<input
							type='time'
							value={blockEnd}
							onChange={(e) => setBlockEnd(e.target.value)}
						/>
					</label>
					<button
						type='button'
						className='mya__addto'
						disabled={!canAddBlock}
						onClick={handleAddBlock}>
						<i className='fa-sharp fa-solid fa-plus' aria-hidden='true' />
						Add blocked time
					</button>
				</div>
				{DAY_KEYS.some((day) => (blocks[day] || []).length > 0) ? (
					<ul className='mya__tolist'>
						{DAY_KEYS.flatMap((day) =>
							(blocks[day] || []).map((window, index) => (
								<li key={`${day}-${index}`} className='mya__toitem'>
									<span className='mya__todate'>{DAY_LABELS[day]}s</span>
									<span className='mya__toreason'>
										{formatTimeKey(window.start)} – {formatTimeKey(window.end)}
									</span>
									<button
										type='button'
										className='mya__toremove'
										onClick={() => handleRemoveBlock(day, index)}>
										Remove
									</button>
								</li>
							))
						)}
					</ul>
				) : (
					<p className='mya__empty'>No weekly blocked times.</p>
				)}
				<button
					type='button'
					className='mya__save'
					disabled={!canSave}
					onClick={handleSave}>
					<i className='fa-sharp fa-solid fa-floppy-disk' aria-hidden='true' />
					{saving ? 'Saving…' : 'Save weekly availability'}
				</button>
			</section>

			<section className='mya__section'>
				<h2 className='mya__title'>Days off</h2>
				<p className='mya__hint'>
					Specific dates you are away — vacations, appointments. These
					are hard blocks: no lesson is ever assigned on them. For a
					partial day (a morning appointment), add From/Until times —
					timed blocks cover one day at a time.
				</p>
				<div className='mya__toform'>
					<label className='mya__field'>
						<span>First day</span>
						<input
							type='date'
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
						/>
					</label>
					<label className='mya__field'>
						<span>Last day (optional)</span>
						<input
							type='date'
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
						/>
					</label>
					<label className='mya__field'>
						<span>From (optional)</span>
						<input
							type='time'
							value={offStart}
							onChange={(e) => setOffStart(e.target.value)}
						/>
					</label>
					<label className='mya__field'>
						<span>Until</span>
						<input
							type='time'
							value={offEnd}
							onChange={(e) => setOffEnd(e.target.value)}
						/>
					</label>
					<label className='mya__field mya__field--reason'>
						<span>Reason (optional)</span>
						<input
							type='text'
							maxLength={200}
							value={reason}
							placeholder='e.g. Out of town'
							onChange={(e) => setReason(e.target.value)}
						/>
					</label>
					<button
						type='button'
						className='mya__addto'
						disabled={!canAddTimeOff}
						onClick={handleAddTimeOff}>
						<i className='fa-sharp fa-solid fa-plus' aria-hidden='true' />
						{savingTimeOff ? 'Adding…' : 'Add days off'}
					</button>
				</div>
				{timeOff.length === 0 ? (
					<p className='mya__empty'>No upcoming days off entered.</p>
				) : (
					<ul className='mya__tolist'>
						{timeOff.map((entry) => (
							<li key={entry.id} className='mya__toitem'>
								<span className='mya__todate'>
									{formatDateKey(entry.date)}
								</span>
								{entry.startTime && entry.endTime && (
									<span className='mya__totime'>
										{formatTimeKey(entry.startTime)} –{' '}
										{formatTimeKey(entry.endTime)}
									</span>
								)}
								{entry.reason && (
									<span className='mya__toreason'>{entry.reason}</span>
								)}
								<button
									type='button'
									className='mya__toremove'
									onClick={() => handleRemoveTimeOff(entry.id)}>
									Remove
								</button>
							</li>
						))}
					</ul>
				)}
			</section>

			{message && <p className='mya__msg'>{message}</p>}
			{error && <p className='mya__msg mya__msg--error'>{error}</p>}
		</div>
	);
}

export default MyAvailability;
